import { Build, Job, CursorPaginated } from "~/components/build/Show/lib/types";
import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import fetcher from "~/components/build/Show/lib/fetcher";
import { FilterByOption } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { setJobQueryData } from "~/components/build/Show/lib/queries/job";

type JobsTableFilters = FilterByOption[];

export const jobsQueryKey = (
  build: Build,
  filters: JobsTableFilters,
  showRetriedJobs: boolean,
  tableSearchValue: string,
) => {
  return [
    "build",
    build.id,
    "jobs",
    {
      filters,
      search: tableSearchValue,
      ...(Features.BuildTableHideRetriedJobs && { showRetriedJobs }),
    },
  ];
};

type JobsPage = CursorPaginated<Job>;
type JobsPageParam = string | null;
type JobsQueryKey = ReturnType<typeof jobsQueryKey>;

type QueryOptionArgs = Omit<
  UseInfiniteQueryOptions<
    JobsPage,
    Error,
    InfiniteData<JobsPage, JobsPageParam>,
    JobsQueryKey,
    JobsPageParam
  >,
  "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
>;

export const jobsQueryOptions = (
  build: Build,
  filters: JobsTableFilters,
  showRetriedJobs: boolean,
  tableSearchValue: string,
  queryOptionsArgs: QueryOptionArgs,
) => {
  return infiniteQueryOptions({
    ...queryOptionsArgs,
    queryKey: jobsQueryKey(build, filters, showRetriedJobs, tableSearchValue),
    queryFn: ({ pageParam, signal, client: queryClient }) =>
      fetchJobs(
        build.buildDataBasePath,
        filters,
        pageParam as string | null,
        tableSearchValue,
        showRetriedJobs,
        signal,
      ).then((data) => {
        data.records.forEach((job) => setJobQueryData(build, job, queryClient));
        return data;
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
  });
};

function fetchJobs(
  buildDataBasePath: Build["buildDataBasePath"],
  filters: JobsTableFilters,
  pageParam: string | null,
  tableSearchValue: string,
  showRetriedJobs: boolean,
  signal: AbortSignal,
) {
  const url = new URL(`${buildDataBasePath}/jobs`, window.location.origin);

  if (tableSearchValue) {
    url.searchParams.set("search", tableSearchValue);
  }

  if (pageParam) {
    url.searchParams.set("after", pageParam);
  }
  if (filters.length > 0) {
    url.searchParams.set("state", filters.join(","));
  }

  // Include retried jobs when the preference is enabled (only with BuildTableHideRetriedJobs)
  if (Features.BuildTableHideRetriedJobs && showRetriedJobs) {
    url.searchParams.set("include_retried_jobs", "true");
  }

  return fetcher<CursorPaginated<Job>>(url, true, { signal });
}
